/**
 * Сезонный пропуск: 30 уровней, бесплатная и премиум-дорожки. Очки дают урожай, рыбалка, задания и т.д.
 * Премиум открывается набором «pk_pass» (оплата СБП) на текущий сезон.
 */
import type { GameState } from '../../core/types';
import {
  MAX_TIER, POINTS_PER_TIER, canClaimPass, claimPassReward, claimablePassCount, ensurePass, passPoints, passReward, passTier, seasonAt,
  type PassTrack,
} from '../../core/season';
import { packById } from '../../core/packs';
import { demoPay, getPending } from '../../pay/client';
import { t, tl } from '../../i18n';
import { h, clearChildren } from '../dom';
import { openPanel } from '../modal';
import { toast } from '../toast';
import { beginPurchase, packLines } from './packs';

export interface PassCtx {
  state: GameState;
  now: () => number;
  petName: (defId: string) => string;
  check: () => Promise<void>;
  onClaimed: () => void;
}

export function openPass(ctx: PassCtx): void {
  let box: HTMLElement | null = null;

  const claim = (tier: number, track: PassTrack): void => {
    const r = claimPassReward(ctx.state, tier, track, ctx.now(), ctx.petName);
    if (r.ok) ctx.onClaimed();
    else toast(r.reason === 'premium' ? t('pass.premium') : t('pass.locked'), 'info');
    render();
  };

  const cell = (tier: number, track: PassTrack): HTMLElement => {
    const sIdx = seasonAt(ctx.now()).index;
    const lines = packLines(passReward(sIdx, tier, track)).join(' · ');
    const can = canClaimPass(ctx.state, tier, track);
    const label = can === true ? t('pass.claim') : can === 'claimed' ? `✅ ${t('pass.owned')}` : can === 'premium' ? `🔒 ${t('pass.premium')}` : `🔒 ${t('pass.locked')}`;
    return h('div', { class: `pass-cell ${track} ${can === 'claimed' ? 'got' : ''} ${can === true ? 'ready' : ''}` },
      h('div', { class: 'pass-lines' }, lines),
      h('button', { class: `btn btn-sm ${can === true ? 'btn-primary' : ''}`, ...(can === true ? {} : { disabled: 'true' }), onclick: () => claim(tier, track) }, label),
    );
  };

  function render(): void {
    if (!box) return;
    clearChildren(box);
    const ps = ensurePass(ctx.state, ctx.now());
    const season = seasonAt(ctx.now());
    const pts = passPoints(ctx.state), tier = passTier(ctx.state);
    const into = pts - tier * POINTS_PER_TIER;
    const pending = getPending();
    const pk = packById('pk_pass');

    box.append(h('div', { class: 'pass-head' },
      h('div', { class: 'pass-season' }, `🎫 ${t('pass.season', { name: tl(season.name), n: season.daysLeft })}`),
      h('div', { class: 'pass-tier' }, t('pass.tier', { n: tier, max: MAX_TIER })),
      h('div', { class: 'pass-bar' }, h('i', { style: `width:${tier >= MAX_TIER ? 100 : Math.min(100, (into / POINTS_PER_TIER) * 100)}%` })),
      h('div', { class: 'pass-hint' }, tier >= MAX_TIER ? '🏁' : t('pass.next', { n: POINTS_PER_TIER - into })),
    ));

    if (ps.premium) box.append(h('div', { class: 'pass-premium on' }, `✅ ${t('pass.active')}`));
    else if (pk) box.append(h('div', { class: 'pass-premium' },
      h('div', { class: 'pass-benefits' }, t('pass.benefits')),
      h('button', {
        class: 'btn btn-primary',
        ...(pending ? { disabled: 'true' } : {}),
        onclick: () => { void beginPurchase(pk).then(() => ctx.check()).then(render); },
      }, `💎 ${t('pass.buy', { rub: pk.rub.toLocaleString('ru-RU') })}`),
      pending && pending.packId === 'pk_pass' ? h('div', { class: 'pass-hint' }, `⏳ ${t('packs.pending')}`) : null,
      pending && pending.packId === 'pk_pass' && pending.demo
        ? h('button', { class: 'btn btn-sm', onclick: () => { void demoPay().then(() => ctx.check()).then(render); } }, t('packs.demo')) : null,
    ));

    const n = claimablePassCount(ctx.state);
    box.append(h('div', { class: 'pass-actions' },
      h('button', {
        class: 'btn btn-sm btn-primary', ...(n > 0 ? {} : { disabled: 'true' }),
        onclick: () => {
          for (let tr = 1; tr <= tier; tr++) for (const track of ['free', 'prem'] as PassTrack[]) {
            const r = claimPassReward(ctx.state, tr, track, ctx.now(), ctx.petName);
            if (r.ok) ctx.onClaimed();
          }
          render();
        },
      }, n > 0 ? `${t('pass.claimAll')} (${n})` : t('pass.nothing')),
    ));

    const list = h('div', { class: 'pass-list' });
    list.append(h('div', { class: 'pass-row pass-row-head' }, h('span', {}, ''), h('span', {}, t('pass.free')), h('span', {}, `💎 ${t('pass.premium')}`)));
    for (let tr = 1; tr <= MAX_TIER; tr++) {
      list.append(h('div', { class: `pass-row ${tr <= tier ? 'reached' : ''} ${tr % 5 === 0 ? 'milestone' : ''}` },
        h('span', { class: 'pass-n' }, String(tr)),
        cell(tr, 'free'),
        cell(tr, 'prem'),
      ));
    }
    box.append(list);
    box.append(h('div', { class: 'pass-hint', style: 'margin-top:8px' }, `${t('pass.points')}. ${t('pass.reset')}`));
  }

  const onUpdate = (): void => render();
  window.addEventListener('pay:update', onUpdate);
  openPanel({
    title: `🎫 ${t('pass.title')}`,
    className: 'panel-wide',
    onClose: () => window.removeEventListener('pay:update', onUpdate),
    body: (container) => { box = container; render(); },
  });
}
